import { useEffect, useState, useRef } from 'react';
import { Trash, Check, Edit3, ArrowLeft } from 'react-feather';
import moment from 'moment';
import TextField from '@mui/material/TextField';
import _ from 'lodash';
import { hapticsImpactLight } from '../../../utils/haptics';
import Tooltip from '@mui/material/Tooltip';
import { UserExam } from '../../../types/User';
import { createSchoolExam } from '../../../services/exams';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store/store';
import { useUserExamUpdate } from '../../../utils/updateUserExam';
import CustomExamDatePicker from '../../Global/CustomExamDatePicker';

interface ExamBlock {
  id: string;
  title: string;
  date: Date | null;
}

interface ExamBlockBuilderProps {
  setCurrentFrameIndex: (index: number) => void;
  cohortExams: UserExam[];
}

const ExamBlockBuilder: React.FC<ExamBlockBuilderProps> = ({
  setCurrentFrameIndex,
  cohortExams,
}) => {
  const currentUser = useSelector((state: RootState) => state.user.profile);
  const school = useSelector((state: RootState) => state.school.school);
  const contentRef = useRef<HTMLDivElement>(null);

  const [blocks, setBlocks] = useState<ExamBlock[]>([]);
  const [newBlockTitle, setNewBlockTitle] = useState('');
  const [newBlockDate, setNewBlockDate] = useState<Date | null>(null);
  const [showAddBlockForm, setShowAddBlockForm] = useState(false);
  const [editingBlockId, setEditingBlockId] = useState<string | null>(null);
  const [editTitle, setEditTitle] = useState('');
  const [editDate, setEditDate] = useState<Date | null>(null);

  const { updateExam } = useUserExamUpdate(undefined, {
    showSuccessMessage: false,
  });

  useEffect(() => {
    if (!_.isNil(editingBlockId)) {
      setNewBlockTitle('');
      setNewBlockDate(null);
      setShowAddBlockForm(false);
    }
  }, [editingBlockId]);

  useEffect(() => {
    if (showAddBlockForm) {
      const titleField = document.getElementById(
        'block-title'
      ) as HTMLInputElement;
      if (titleField) {
        titleField.focus();
      }
    }
  }, [showAddBlockForm]);

  const handleAddBlock = () => {
    if (!newBlockTitle) return;

    const newBlock: ExamBlock = {
      id: Math.random().toString(36).substr(2, 9),
      title: newBlockTitle,
      date: newBlockDate ? new Date(newBlockDate) : null,
    };

    setBlocks([...blocks, newBlock]);
    setNewBlockTitle('');
    setNewBlockDate(null);
    setShowAddBlockForm(false);

    // Scroll to bottom after adding block
    setTimeout(() => {
      if (contentRef.current) {
        contentRef.current.scrollTop = contentRef.current.scrollHeight;
      }
    }, 300);
  };

  const handleRemoveBlock = (id: string) => {
    if (!_.isNil(editingBlockId)) return;
    setBlocks(blocks.filter((block) => block.id !== id));
  };

  const handleEditBlock = (block: ExamBlock) => {
    if (!_.isNil(editingBlockId)) return;
    setEditingBlockId(block.id);
    setEditTitle(block.title);
    setEditDate(block.date ? new Date(block.date) : null);
  };

  const handleSaveEdit = () => {
    if (!editingBlockId || !editTitle) return;

    setBlocks(
      blocks.map((block) =>
        block.id === editingBlockId
          ? {
              ...block,
              title: editTitle,
              date: editDate ? new Date(editDate) : null,
            }
          : block
      )
    );
    setEditingBlockId(null);
    setEditTitle('');
    setEditDate(null);
  };

  const createAllExamsFromBlocks = async () => {
    if (blocks.length === 0) {
      const firstCohortExam = cohortExams[0];
      if (firstCohortExam) {
        await setPrimaryExam(firstCohortExam.id);
        setCurrentFrameIndex(2);
        return;
      } else {
        console.error('No cohort exams found');
      }
    }

    if (!school?.id) return;

    try {
      // Create all exams and get first exam ID
      const firstExam = await createExamsFromBlocks();

      // Set first exam as primary if created successfully
      if (firstExam && currentUser) {
        await setPrimaryExam(firstExam.id);
      }
    } catch (error) {
      console.error('Error creating exams from blocks:', error);
    } finally {
      setCurrentFrameIndex(2);
    }
  };

  const createExamsFromBlocks = async () => {
    if (!school?.id) return;
    let firstExam;
    for (const block of blocks) {
      const newExam = await createSchoolExam(
        block.title,
        moment.utc(block.date),
        school.id
      );
      if (!firstExam) {
        firstExam = newExam;
      }
    }
    return firstExam;
  };

  const setPrimaryExam = async (examId: string) => {
    if (!currentUser) return;
    await updateExam({
      userId: currentUser.id,
      examId: examId,
      primary: true,
    });
  };

  return (
    <div className="full-custom-frame">
      <div className="full-custom-frame_content" ref={contentRef}>
        <h1>Add your curriculum's blocks</h1>
        <p>Let's build a scaffold for your curriculum.</p>
        {cohortExams && cohortExams.length > 0 && (
          <div className="cohort-exams">
            <p>Previously Created Exams</p>
            <div className="blocks-list">
              {cohortExams
                .sort((a, b) => {
                  const dateA = a.examDate ? new Date(a.examDate) : null;
                  const dateB = b.examDate ? new Date(b.examDate) : null;

                  if (!dateA || !dateB) return 0;
                  return dateA.getTime() - dateB.getTime();
                })
                .map((exam) => (
                  <div key={exam.id} className="block-item">
                    <div className="block-content">
                      <span className="block-title">{exam.name}</span>
                      <span className="block-date">
                        {exam.examDate
                          ? moment(exam.examDate).format('MMMM D, YYYY')
                          : 'No date set'}
                      </span>
                    </div>
                  </div>
                ))}
            </div>
          </div>
        )}
        <div className="exam-block-builder">
          <div className="blocks-list">
            {blocks
              .sort((a, b) => {
                if (!a.date || !b.date) return 0;
                return a.date.getTime() - b.date.getTime();
              })
              .map((block) => (
                <div
                  key={block.id}
                  className={`block-item ${
                    editingBlockId === block.id ? 'editing' : ''
                  }`}
                >
                  {editingBlockId === block.id ? (
                    <div className="block-content">
                      <TextField
                        label="Block Title"
                        value={editTitle}
                        onChange={(e) => setEditTitle(e.target.value)}
                        size="small"
                        tabIndex={0}
                        aria-label="Block Title"
                      />
                      <CustomExamDatePicker
                        value={editDate ? moment(editDate) : null}
                        onChange={(date) => setEditDate(date?.toDate() || null)}
                        inline={false}
                      />
                      <div className="block-actions">
                        <button
                          className="button button--icon-only"
                          onClick={handleSaveEdit}
                          disabled={!editTitle}
                          tabIndex={0}
                          aria-label="Save Block"
                        >
                          <Check />
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="block-content">
                        <span className="block-title">{block.title}</span>
                        <span className="block-date">
                          {block.date
                            ? moment(block.date).format('MMMM D, YYYY')
                            : 'No date set'}
                        </span>
                      </div>
                      <div className="block-actions">
                        <button
                          className="button button--icon-only button--edit"
                          onClick={() => handleEditBlock(block)}
                          disabled={!_.isNil(editingBlockId)}
                        >
                          <Edit3 />
                        </button>
                        <button
                          className="button button--icon-only button--remove"
                          onClick={() => handleRemoveBlock(block.id)}
                          disabled={!_.isNil(editingBlockId)}
                        >
                          <Trash />
                        </button>
                      </div>
                    </>
                  )}
                </div>
              ))}
          </div>
          {showAddBlockForm && (
            <div className="add-block-form">
              <TextField
                id="block-title"
                label="Block Title"
                value={newBlockTitle}
                onChange={(e) => setNewBlockTitle(e.target.value)}
              />
              <CustomExamDatePicker
                value={newBlockDate ? moment(newBlockDate) : null}
                onChange={(date) => setNewBlockDate(date?.toDate() || null)}
                inline={false}
              />
              <div className="block-actions">
                <button
                  className="button"
                  onClick={handleAddBlock}
                  disabled={
                    !newBlockTitle ||
                    moment(newBlockDate) <
                      moment().subtract(4, 'years').startOf('day') ||
                    moment(newBlockDate) >
                      moment().add(4, 'years').startOf('day')
                  }
                >
                  Add
                </button>
              </div>
            </div>
          )}
          {!showAddBlockForm && (
            <button
              className="button button--add-block"
              onClick={() => {
                setShowAddBlockForm(true);
                hapticsImpactLight();
              }}
              disabled={!_.isNil(editingBlockId)}
              tabIndex={0}
              aria-label="Add Block"
            >
              Add Block
            </button>
          )}
        </div>
      </div>
      <div className="full-custom-frame_actions">
        <button
          className="button button--secondary button--prev"
          onClick={() => {
            setCurrentFrameIndex(0);
            hapticsImpactLight();
          }}
        >
          <ArrowLeft />
        </button>
        <Tooltip
          title={
            blocks.length === 0
              ? 'You need to add at least one block to start building your curriculum.'
              : ''
          }
          enterTouchDelay={0}
        >
          <span>
            <button
              className="button button--start-building"
              onClick={() => {
                createAllExamsFromBlocks();
                hapticsImpactLight();
              }}
              disabled={cohortExams.length === 0 && blocks.length === 0}
            >
              Continue
            </button>
          </span>
        </Tooltip>
      </div>
    </div>
  );
};

export default ExamBlockBuilder;
